/**
 * Registry of per-entity-type create-modal overrides.
 *
 * The generic create slideout (`CrmCreateForm`) works for most CRM entity types — the
 * properties + associations declared on the entity type are all the user
 * needs to fill in. Some entity types (notes/tasks today, potentially more in
 * future) need a richer, opinionated create flow: a custom layout, additional
 * controls, conditional fields, or a different submit pipeline.
 *
 * This module is the SINGLE entry point for "is the default create modal
 * overridden for this entity type?" — adding a new override is a one-line
 * registry entry. Consumers should not check entity_type_name against
 * hardcoded strings; they should call {@link getCrmCreateOverride}.
 *
 * ## Contract for override components
 *
 * Override components own their own modal chrome (Modal / Dialog / overlay)
 * and submit pipeline. They receive:
 *   - `orgId`           : Active org. Use for event submission and queries.
 *   - `isOpen`          : Whether the modal should be open. Drive `<Modal>` /
 *                         `<DialogTrigger>` from this so close-on-success and
 *                         close-on-cancel both call `onClose`.
 *   - `onClose`         : Call to dismiss the modal. The parent will set its
 *                         own open state to false and unmount the modal on
 *                         the next render.
 *   - `onSuccess`       : Optional. Call with the newly-created entity_id
 *                         after a successful submit so parents can refetch
 *                         their list / scroll to the new row / etc.
 *
 * The parent (CRMObjectListPage) is responsible for:
 *   - Discovering the override via {@link getCrmCreateOverride}.
 *   - Owning the boolean open state.
 *   - Replacing the default "+ Add" click handler with one that flips the
 *     boolean to true. This takes precedence over any caller-supplied
 *     `onCreateClick` so registry-level overrides remain authoritative.
 *
 * ## Adding a new override
 *
 * 1. Build the component (typically `lib/crm/<feature>/<Feature>CreateModal.tsx`).
 *    It must accept {@link CrmCreateOverrideProps} and render its own Modal.
 * 2. Add an entry to {@link CRM_CREATE_OVERRIDES} keyed by entity_type_name.
 * 3. Done — no caller changes needed.
 */

import type { FC } from 'react';

import { MemoCreateComposeOverride } from '@/lib/crm/record/memo/MemoCreateComposeOverride';
import {
  isMemoEntityType,
  type MemoEntityType,
} from '@/lib/crm/record/memo/notes.utils';

/**
 * Generic defaults a caller can pre-fill on the override modal.
 *
 * The field set is intentionally small and additive: callers (e.g. the
 * pipeline-stage `+` button) provide whatever defaults they know about, and
 * each override component reads only the keys it cares about. Adding new
 * keys here is a non-breaking change as long as existing overrides ignore
 * unknown keys.
 *
 * Keep names neutral across entity types: `stageEntityId` works for any
 * pipelineable entity (notes / tasks today; deals later), `ownerEntityId`
 * works for any owner-bearing entity, etc.
 */
export interface CrmCreateDefaults {
  /** Pre-select a pipeline stage (e.g. the column the user clicked `+` on). */
  stageEntityId?: string;
  /** Pre-associate a meeting (e.g. "Add note" on a dashboard meeting row). */
  meetingEntityId?: string;
}

/**
 * Props every create-modal override receives.
 *
 * See module docstring for the full contract.
 */
export interface CrmCreateOverrideProps {
  /** Active org id; required for event submission and queries. */
  orgId: number;
  /**
   * The entity type this override is creating. The same override component is
   * registered for several types (notes and tasks share one), so it needs the
   * type explicitly rather than inferring it — a task must be created as an
   * ahoy_task regardless of whether a stage was pre-filled.
   *
   * Constrained to the memo family (`ahoy_note | ahoy_task`) — the only types
   * with a registered override — so a timeline alias or other arbitrary string
   * can never be routed into the create pipeline. Callers must narrow before
   * passing (see `getCrmCreateOverride`, which returns `undefined` for any
   * non-memo type).
   */
  entityTypeName: MemoEntityType;
  /** Whether the modal should be open. */
  isOpen: boolean;
  /** Called when the user dismisses the modal (cancel, ESC, backdrop click). */
  onClose: () => void;
  /**
   * Optional. Called after a successful create with the new entity's id.
   * Parents typically use this to refetch the list and surface the new row.
   */
  onSuccess?: (entityId: string) => void;
  /**
   * Optional pre-fill values supplied by the caller. Examples:
   *   - Pipeline column `+` button → `{ stageEntityId: <column stage id> }`
   *   - Future: contact slideout "+ Add note" → `{ ownerEntityId: <user> }`
   * Overrides should ignore keys they don't understand.
   */
  defaults?: CrmCreateDefaults;
}

/**
 * Click-handler signature for opening a create modal.
 *
 * Used by `CRMObjectListPageProps.onCreateClick` and the equivalent prop on
 * `PipelinePageProps`. Optional `defaults` lets callers (e.g. pipeline-stage
 * `+` buttons) pre-fill state without coupling them to a specific override.
 * Existing `() => void` consumers remain compatible because the arg is
 * optional and ignored if unused.
 */
export type CrmCreateClickHandler = (defaults?: CrmCreateDefaults) => void;

/**
 * Registry of entity types whose default create modal should be replaced.
 *
 * Keep this map flat and explicit — searching for `CRM_CREATE_OVERRIDES`
 * surfaces every override at a glance during code review.
 */
const CRM_CREATE_OVERRIDES: Partial<
  Record<MemoEntityType, FC<CrmCreateOverrideProps>>
> = {
  ahoy_note: MemoCreateComposeOverride,
  // Tasks are created through the same composer: on a migrated org a staged
  // composition saves as an ahoy_task (see useMemoCreate), so the task
  // board's "+" and the tasks list's "Add task" open the note composer with
  // the column's stage pre-selected rather than the generic create form.
  ahoy_task: MemoCreateComposeOverride,
};

/**
 * Resolve the create-modal override component for an entity type.
 *
 * Returns `undefined` when no override is registered — callers should fall
 * back to the default create slideout in that case.
 */
export function getCrmCreateOverride(
  entityTypeName: string
): FC<CrmCreateOverrideProps> | undefined {
  // Reject anything outside the memo family (timeline aliases, other entity
  // types) up front, so only a validated `ahoy_note | ahoy_task` ever reaches
  // the registry lookup — and the returned override's `entityTypeName` prop
  // stays a `MemoEntityType`.
  if (!isMemoEntityType(entityTypeName)) {
    return undefined;
  }
  return CRM_CREATE_OVERRIDES[entityTypeName];
}
